import { useEffect, useState, type ReactElement } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";

import { Button, IconButton } from "../components/Button";
import { ChangeTodaySheet } from "../components/ChangeTodaySheet";
import { CycleSheet } from "../components/CycleSheet";
import { Hero } from "../components/Hero";
import { I, SESSION_ICON } from "../components/icons";
import { useInstallPrompt } from "../components/InstallBar";
import { LogWeightSheet } from "../components/LogWeightSheet";
import { Notice } from "../components/Notice";
import { ProgressBar } from "../components/ProgressBar";
import { Ring } from "../components/Ring";
import { NavRow, RowGroup } from "../components/Rows";
import { SetupSheet } from "../components/SetupSheet";
import { Screen } from "../components/Shell";
import { Stat, StatStrip } from "../components/StatStrip";
import { ago } from "../components/StepsRefresh";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { useUpdateReady } from "../components/UpdateBar";
import { rings } from "../domain/activity";
import { cycleInfo, cycleOn, cyclePhase } from "../domain/cycle";
import { forecastFor } from "../domain/cyclePatterns";
import { noticedLine, noticedOfferFor, withNoticedOff } from "../domain/noticedWalks";
import { cycleSuggestion, keptKey, loggedWords, offerMinutes, type CycleOffer } from "../domain/cycleTraining";
import { PATTERN_WORDS } from "../domain/cycleWords";
import { clockTime, dayLabel, dayMonth, fmtClock, parseDay, todayStr, weekKey } from "../domain/date";
import { measuredSince, sessionKcal, stepBreakdown, strideMeters } from "../domain/energy";
import { cravingLine } from "../domain/eatingQuestions";
import { entriesFor, foodOn } from "../domain/intake";
import { dayLedger } from "../domain/ledger";
import { doseIndex, phase, sessionFor, sessionKeyFor, typeName, weekNo } from "../domain/plan";
import { heatWindow, mealWalkRow, programmeFor } from "../domain/programme";
import { dayProgress, sessionMinutes, sessionPct } from "../domain/queue";
import { SESSION_LABEL } from "../domain/session";
import { missedDays, recapDue, streak, streakLine, weekStats } from "../domain/stats";
import { nextUp, setupSteps, weighInDue, workParts } from "../domain/today";
import { sessionGate } from "../domain/trainingQuestions";
import { cycleVariant, movesDone, stepTone } from "../domain/todayView";
import { hasTreadmill, tmProtocol, tmTotalSecs } from "../domain/treadmill";
import { weightSeries } from "../domain/trend";
import { MORNING_WORDS, morningAsk, morningAskLine } from "../domain/readiness";
import { dayMinutes } from "../domain/week";
import { waterRowValue } from "../domain/water";
import { walkSecs } from "../domain/walk";
import { pingSession, resumeSessionPing } from "../lib/appBridge";
import { beep, cancelNotification, isIOS, isStandalone } from "../lib/device";
import { applyUpdate } from "../lib/swUpdate";
import { useDocumentVisible } from "../lib/useDocumentVisible";
import { touchDay } from "../store/dayLog";
import { useDeviceAnswers, useKaya, usePlayerMode, useTrainingDevice } from "../store/kayaStore";
import { usePlayerIntent } from "../store/playerIntent";
import { addNoticedWalk, keepMySession, setMorningAfter, setPeriodStart, setSessionOverride } from "../store/toggles";
import "./Today.css";

/** The sync state as a 44px dot at the right of the header; a tap syncs now. */
function SyncDot() {
  const { user, syncStatus, online, syncNow, lastSync } = useKaya();
  if (!user)
    return (
      <span className="today-sync" role="img" aria-label="Kept on this device only">
        <span className="today-sync__dot today-sync__dot--off" />
      </span>
    );
  const state =
    !online || syncStatus === "offline"
      ? { word: "Offline", cls: "off" }
      : syncStatus === "syncing"
        ? { word: "Syncing", cls: "busy" }
        : syncStatus === "error"
          ? { word: "Sync failed", cls: "err" }
          : { word: `Synced${lastSync ? ` ${ago(lastSync)}` : ""}`, cls: "ok" };
  return (
    <IconButton
      className="today-sync"
      label={`${state.word}. Sync now`}
      onClick={() => void syncNow({ force: true })}
      icon={<span className={`today-sync__dot today-sync__dot--${state.cls}`} aria-hidden="true" />}
    />
  );
}

/**
 * Today: what to do next, how the day is going, and the day's items (audit §4).
 *
 * One hero from `nextUp` — the open session outranks everything, then the scheduled session, the
 * treadmill, and the day done or rested. At most two notices, each only when it can be acted on.
 * The stat strip is the junction to Activity and Food; the rows are the day's items, each a link.
 * Level, challenge, badges, the quote and the weight chart live on Progress now.
 */
export default function Today() {
  const history = useHistory();
  const location = useLocation();
  const { S, meta, update, showToast, checkBadges, session, endSession, setupSnoozed, snoozeSetup } =
    useKaya();
  const device = useDeviceAnswers();
  const pm = usePlayerMode();
  const intent = usePlayerIntent();
  const visible = useDocumentVisible();
  const updateReady = useUpdateReady();
  const [now, setNow] = useState(() => Date.now());
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [updating, setUpdating] = useState(false);
  const { canInstall, install } = useInstallPrompt();
  const [installShut, setInstallShut] = useState(false);
  const [sheet, setSheet] = useState<null | "change" | "cycle" | "weight" | "setup">(null);

  const running = session !== null && (session.kind === "workout" || session.since !== null);
  // The open session's clock, ticking only while it can be seen and is moving.
  useEffect(() => {
    if (!session || !visible || !running) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [session, visible, running]);
  const sid = session?.sid;
  useEffect(() => setConfirmEnd(false), [sid]);

  const ds = todayStr();
  const today = new Date();
  const L = S.logs[ds];
  // the safety answers swap or stop the day's session, as on Train (P9, sessionGate)
  const gate = sessionGate(S, sessionKeyFor(S, ds), device, today);
  const key = gate.key;
  const s = sessionFor(key);
  const p = phase(S);
  const pct = sessionPct(S, ds, key);
  const next = nextUp(S, ds, session, now, device, pm.simple);
  const st = streak(S);
  const treadmill = hasTreadmill(S);
  const food = foodOn(S);
  // The parent's rhythm (ADR-052): the walk after a meal and the summer's hours. The owner's Today
  // is unchanged — both are empty off the Steady programme.
  const steady = !!S.profile.training && programmeFor(S, ds) === "steady";
  const mealWalks = steady ? mealWalkRow(S, ds, today.getTime()) : [];
  const heat = steady ? heatWindow(ds, today.getHours() + today.getMinutes() / 60) : null;

  /** A player's Minimise and End return to the page it was started from. */
  const from = { from: location.pathname };
  /** One session at a time: with one open, every Start goes back to it. (ADR-027) */
  const startOrResume = (go: () => void) => {
    if (session) history.push(session.route, from);
    else go();
  };
  // beeping inside the click is also what unlocks the AudioContext for the player
  const startWalk = () =>
    startOrResume(() => {
      beep(880, 120, 0.15, S.profile.sound);
      history.push("/walk", from);
    });
  const startTreadmill = () =>
    startOrResume(() => {
      beep(880, 120, 0.15, S.profile.sound);
      history.push("/treadrun", from);
    });
  const startSession = () => startOrResume(() => history.push("/workout", from));

  // The cycle line for today's session, null on most days (ADR-057). An offer starts the session
  // it names; "Keep my session" only puts the weekday's session back, held for the day.
  const trainingDevice = useTrainingDevice();
  const cyc = cycleSuggestion(S, ds);
  const takeOffer = (o: CycleOffer) =>
    startOrResume(() => {
      if (o.how === "short") intent.setShort();
      else update((d) => setSessionOverride(d, ds, o.key));
      history.push("/workout", from);
    });
  const keepSession = () => {
    update((d) => keepMySession(d, ds));
    showToast("Your usual session is back for today");
  };

  /* ---------- hero ---------- */

  let hero: ReactElement;
  if (next.kind === "session-open") {
    const open = next.session;
    const label = SESSION_LABEL[open.kind];
    // A workout keeps its clock in the day log, as the LiveBar reads it.
    const elapsed =
      open.kind === "workout" ? L?.[open.rescue || open.extra ? "extraElapsed" : "elapsedMs"] || 0 : next.elapsedMs;
    const samples = S.sampleCache?.date === ds ? S.sampleCache.samples : [];
    const soFar = open.kind === "workout" ? null : measuredSince(samples, Date.parse(open.startedAt), now);
    // Resume is a real tap: Chrome only follows the counter app's `intent:` from a gesture.
    const resume = () => {
      resumeSessionPing(open, strideMeters(S), pm);
      history.push(open.route, from);
    };
    const end = () => {
      if (open.kind !== "workout") pingSession("stop", Date.now(), open.sid);
      // A run minimised mid-phase still has its phase-change notification queued.
      if (open.kind === "treadmill") cancelNotification("tm");
      endSession();
    };
    hero = (
      <Hero
        ariaLabel={`${label} in progress`}
        label={running ? `${label} in progress` : `${label} paused`}
        figure={fmtClock(elapsed)}
        unit="elapsed"
        line={
          open.kind === "workout" ? undefined : soFar === null ? (
            "Steps pending from your phone"
          ) : (
            <>
              {soFar.toLocaleString()} steps so far <Tag tone="phone" />
            </>
          )
        }
        actions={
          confirmEnd ? (
            <>
              <Button variant="danger" grow onClick={end}>
                End {label.toLowerCase()}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmEnd(false)}>
                Keep going
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setConfirmEnd(true)}>
                End
              </Button>
              <Button grow icon={I.play} onClick={resume}>
                {`Resume ${label.toLowerCase()}`}
              </Button>
            </>
          )
        }
      />
    );
  } else if (next.kind === "session") {
    const done = movesDone(next.pct, next.moves);
    // The cycle line (ADR-057): the reason under the figures, and its offer or "Keep my session"
    // in Change's place. A period day names the session it stands in for.
    const kept = cyc?.keep ? keptKey(S, ds) : null;
    const offer = cyc?.offer ?? null;
    hero = (
      <Hero
        ariaLabel="Next up"
        label={cyc?.kind === "period" ? `Next up · ${cyc.reason}` : "Next up"}
        title={next.name}
        line={`${next.minutes} min · ${next.moves} moves`}
        sub={
          kept ? (
            `in place of ${typeName(kept)} · ${sessionMinutes(S, kept, doseIndex(S, kept, ds))} min`
          ) : // a Steady person sees minutes: no kcal, no streak (plan §1.15, §3.4)
          steady ? undefined : (
            <>
              ~{next.kcalEst.toLocaleString()} kcal est. ·{" "}
              {st > 0 ? `${st}-day streak` : L || Object.keys(S.logs).length ? streakLine(0) : "Your first session"}
            </>
          )
        }
        note={gate.line ?? (cyc ? cyc.line : undefined)}
        visualSide
        visual={
          <span role="img" aria-label={`${done} of ${next.moves} moves done`}>
            <Ring pct={next.pct} size={72} stroke={7} color="var(--brand)" figure={`${done}/${next.moves}`} />
          </span>
        }
        actions={
          <>
            {kept ? (
              <Button variant="ghost" onClick={keepSession}>
                Keep my session
              </Button>
            ) : offer ? (
              <Button variant="ghost" onClick={() => takeOffer(offer)}>
                {`${offer.label} · ${offerMinutes(S, offer, trainingDevice)} min`}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setSheet("change")}>
                Change
              </Button>
            )}
            <Button grow icon={I.play} onClick={startSession}>
              {done > 0 ? `Continue · ${done} of ${next.moves}` : kept ? "Start gentle" : "Start session"}
            </Button>
          </>
        }
      />
    );
  } else if (next.kind === "treadmill") {
    hero = (
      <Hero
        ariaLabel="Next up"
        label="Next up"
        title={`Evening treadmill · ${next.minutes} min`}
        line={next.protocolLine}
        sub={`~${next.kcalEst.toLocaleString()} kcal est.`}
        actions={
          <Button grow icon={I.play} onClick={startTreadmill}>
            Start treadmill
          </Button>
        }
      />
    );
  } else {
    const steps = stepBreakdown(S, ds);
    const goal = rings(S, ds).steps.goal;
    const tone = stepTone(steps.basis);
    const counted = steps.basis === "none" ? null : steps.total;
    hero = (
      <Hero
        ariaLabel={next.kind === "done" ? "Day done" : "Rest day"}
        label={
          next.kind === "done"
            ? "Day done"
            : L?.periodSkip
              ? "Rest day · streak safe"
              : "Rest day · an easy walk is enough"
        }
        figure={counted}
        unit={<>steps {tone ? <Tag tone={tone} /> : null}</>}
        line={`of ${goal.toLocaleString()} goal${st > 0 ? ` · ${st}-day streak` : ""}`}
        visual={
          <ProgressBar
            value={counted}
            max={goal}
            ariaLabel={
              counted === null
                ? "No steps counted yet"
                : `${counted.toLocaleString()} steps of a ${goal.toLocaleString()} goal`
            }
          />
        }
        actions={
          <>
            {next.kind === "rest" ? (
              <Button variant="ghost" onClick={() => setSheet("change")}>
                Change
              </Button>
            ) : null}
            <Button grow icon={I.walk} onClick={startWalk}>
              Start a walk
            </Button>
          </>
        }
      />
    );
  }

  /* ---------- notices: at most two, each only when it can be acted on ----------
     A waiting update is shown above them all and outside the cap: it used to be the fourth
     candidate, so a day with a rescue and a predicted period kept an installed app on the old
     build indefinitely. */

  const missed = missedDays(S);
  const lastMissed = missed.length ? missed[missed.length - 1] : null;
  const ci = cycleOn(S) ? cycleInfo(S, ds) : null;
  const setup = setupSteps(S, ds, { treadmillAnswered: meta.setupTreadmillAnswered });
  const setupDone = setup.filter((x) => x.done).length;
  // "Later" hides it until the app is next opened; the sheet's "Done for now" puts it away.
  const showSetup = setupDone < setup.length && !meta.setupDismissedAt && !setupSnoozed;

  const notices: { id: string; el: ReactElement }[] = [];
  if (lastMissed && !L?.rescueDone) {
    notices.push({
      id: "rescue",
      el: (
        <Notice
          key="rescue"
          tone="caution"
          icon={I.flame}
          primary={{
            label: "Rescue session",
            onClick: () =>
              startOrResume(() => {
                update((d) => void (d.rescueFor = lastMissed));
                intent.setRescue();
                history.push("/workout", from);
              }),
          }}
          secondary={
            S.freezes > 0
              ? {
                  label: `Use a freeze (${S.freezes})`,
                  onClick: () => {
                    update((d) => {
                      d.freezes--;
                      touchDay(d, lastMissed).frozen = true;
                    });
                    showToast("Streak protected");
                  },
                }
              : undefined
          }
        >
          Missed{" "}
          {parseDay(lastMissed).toLocaleDateString("en-GB", {
            weekday: "long",
          })}{" "}
          — a 15-minute rescue counts it.
        </Notice>
      ),
    });
  }
  if (ci?.predicted) {
    notices.push({
      id: "period",
      el: (
        <Notice
          key="period"
          tone="cycle"
          primary={{
            label: "Period started",
            onClick: () => {
              update((d) => setPeriodStart(d, ds, true));
              showToast("Logged — gentle plan for today");
              checkBadges();
            },
          }}
        >
          Period expected around now.
        </Notice>
      ),
    });
  }
  // Her own pattern, on the days it usually comes (ADR-060): words to read, never a swap. A
  // predicted or started period has the slot, and a symptom already logged today drops out.
  const forecast = ci && !ci.predicted && !ci.inPeriod ? forecastFor(S, ds) : null;
  if (forecast) {
    notices.push({
      id: "pattern",
      el: (
        <Notice
          key="pattern"
          tone="cycle"
          icon={I.drop}
          primary={{ label: PATTERN_WORDS.notice, onClick: () => history.push(forecast.link) }}
        >
          {forecast.text}
        </Notice>
      ),
    });
  }
  // The morning after a session that hurt (ADR-065): asked once, today only; "Still sore" counts
  // that session over the pain limit for the joint (readiness.ts::painRule).
  const morning = morningAsk(S, ds);
  if (morning) {
    const answer = (ok: boolean) => update((d) => setMorningAfter(d, ds, morning.ds, morning.joint, ok));
    notices.push({
      id: "morning",
      el: (
        <Notice
          key="morning"
          tone="caution"
          title={MORNING_WORDS.title}
          primary={{ label: MORNING_WORDS.ok, onClick: () => answer(true) }}
          secondary={{ label: MORNING_WORDS.sore, onClick: () => answer(false) }}
        >
          {morningAskLine(morning)}
        </Notice>
      ),
    });
  }
  // A walk the counter noticed (ADR-063): offered, saved only on a tap; "No" is this phone's.
  const noticed = noticedOfferFor(S, ds, meta.noticedOff, next.kind === "session-open" && next.session.kind !== "workout" ? { startedAt: next.session.startedAt, secs: next.elapsedMs / 1000 } : null);
  if (noticed) {
    notices.push({
      id: "noticed",
      el: (
        <Notice
          key="noticed"
          icon={I.footsteps}
          primary={{ label: "Save as a walk", onClick: () => update((d) => addNoticedWalk(d, ds, noticed, Date.now())) }}
          secondary={{ label: "No", onClick: () => useKaya.getState().setWalkMeta({ noticedOff: withNoticedOff(meta.noticedOff, noticed.startedAt) }) }}
        >
          {noticedLine(noticed, clockTime)} — was that a walk?
        </Notice>
      ),
    });
  }
  if (showSetup) {
    notices.push({
      id: "setup",
      el: (
        <Notice
          key="setup"
          primary={{ label: "Set up", onClick: () => setSheet("setup") }}
          secondary={{ label: "Later", onClick: snoozeSetup }}
        >
          Finish setting up · {setupDone} of {setup.length}
        </Notice>
      ),
    });
  }
  // The questionnaire's craving answer: one if-then line before the usual time, gone once the
  // snack it suggests is logged (ADR-042). A notice, not a block: most of the day it is absent.
  const craving = food ? cravingLine(S, today, entriesFor(S, ds).some((e) => e.meal === "snack")) : null;
  if (craving) {
    notices.push({
      id: "craving",
      el: (
        <Notice key="craving" icon={I.bowl} primary={{ label: "Add a snack", onClick: () => history.push("/food") }}>
          {craving}
        </Notice>
      ),
    });
  }
  // The walk after a meal leads the notices while it is due: it is timed off the meal, and gone
  // once a walk is logged or the window has passed (plan §3.3).
  const dueWalk = mealWalks.find((w) => w.state === "due");
  if (dueWalk) {
    notices.unshift({
      id: "mealwalk",
      el: (
        <Notice key="mealwalk" icon={I.footsteps} primary={{ label: "Log a walk", onClick: startWalk }}>
          Walk 10 minutes after {dueWalk.meal}
          {S.profile.eating?.conditions?.some((c) => c === "diabetes" || c === "prediabetes") ? " — it eases the rise in blood sugar" : ""}.
          {heat && !heat.outdoors ? ` In ${heat.month}, walk indoors until sunset.` : ""}
        </Notice>
      ),
    });
  }
  if (gate.stop && gate.line) {
    notices.unshift({ id: "nosession", el: <Notice key="nosession" tone="caution">{gate.line}</Notice> });
  }
  // Android has a real prompt; Safari has none, so iOS gets the Share → Add to Home Screen words.
  if (!isStandalone() && !installShut && (canInstall || isIOS())) {
    notices.push({
      id: "install",
      el: canInstall ? (
        <Notice
          key="install"
          icon={I.dl}
          primary={{ label: "Install Kaya", onClick: () => void install() }}
          onDismiss={() => setInstallShut(true)}
        >
          Install Kaya for full screen, offline use and a home-screen icon.
        </Notice>
      ) : (
        <Notice key="install" icon={I.dl} secondary={{ label: "Got it", onClick: () => setInstallShut(true) }}>
          Add Kaya to your home screen: Share, then Add to Home Screen.
        </Notice>
      ),
    });
  }
  const updateNotice = updateReady ? (
    <Notice
      key="update"
      icon={I.refresh}
      primary={{
        label: updating ? "Updating…" : "Refresh",
        onClick: () => {
          if (updating) return;
          setUpdating(true);
          void applyUpdate();
        },
      }}
    >
      A new version of Kaya is ready. Your data is untouched.
    </Notice>
  ) : null;
  const shown = notices.slice(0, 2);
  // Setup is where a first run starts, so it sits above the hero; the others follow it.
  const setupFirst = shown.find((n) => n.id === "setup");
  const after = shown.filter((n) => n.id !== "setup");

  /* ---------- stat strip ---------- */

  const steps = stepBreakdown(S, ds);
  const stepsTone = stepTone(steps.basis);
  const work = workParts(S, ds);
  const ledger = food ? dayLedger(S, ds, undefined, device) : null;
  const ring = rings(S, ds);
  const moveKnown = work.total !== null || ring.move.value > 0;

  /* ---------- rows ---------- */

  const walks = L?.walks || [];
  const moves = dayProgress(S, ds, key)?.total ?? 0;
  // the real length, of the queue the day froze once it began (ADR-050)
  const realMin = dayMinutes(S, ds, key, pm.simple);
  const sessionRow = (() => {
    if (next.kind === "session") return null;
    let sub: string;
    if (gate.stop) sub = "No session today";
    else if (L?.periodSkip) sub = "Rest · streak safe";
    else if (key === "R") sub = "Rest day · nothing scheduled";
    else if (pct !== null && pct >= 1) {
      const mins = L?.elapsedMs ? Math.round(L.elapsedMs / 60000) : realMin;
      // a Steady person sees minutes, never kcal (plan §3.4)
      sub = steady ? `Done · ${mins} min` : `Done · ${mins} min · ~${(L?.kcal || sessionKcal(S, key, 1)).toLocaleString()} kcal`;
    } else if (pct) sub = `${movesDone(pct, moves)} of ${moves} moves done · ${realMin} min`;
    else sub = `Not started · ${realMin} min`;
    return (
      <NavRow
        key="session"
        title={key === "R" ? "Rest day" : typeName(key)}
        subtitle={sub}
        icon={I[SESSION_ICON[s.type]]}
        tone="brand"
        to="/train/session"
      />
    );
  })();

  const treadmillRow =
    treadmill && next.kind !== "treadmill" ? (
      <NavRow
        key="treadmill"
        title="Treadmill"
        subtitle={
          L?.treadmill
            ? `Done${L.treadmill.km ? ` · ${L.treadmill.km.toFixed(1)} km` : ""}`
            : `${Math.round(tmTotalSecs(tmProtocol(S, gate.easyTreadmill)) / 60)} min · not started`
        }
        icon={I.treadmill}
        tone="caution"
        to="/train/treadmill"
      />
    ) : null;

  const walksOpen = session?.kind === "walk";
  const walksRow = (
    <NavRow
      key="walks"
      title="Walks"
      subtitle={
        <>
          {walks.length
            ? `${walks.length}${walksOpen ? " earlier" : ""} · ${fmtClock(walkSecs(walks) * 1000)}`
            : walksOpen
              ? "One in progress"
              : "Start a walk"}
          {heat ? <span className="today-heat">{heat.line}</span> : null}
        </>
      }
      icon={I.footsteps}
      tone="sky"
      to="/train/walks"
    />
  );

  // The walks after meals not yet due, or done: the due one is the notice above.
  const mealWalkRows = mealWalks
    .filter((w) => w.state === "later" || w.state === "done")
    .map((w) => (
      <NavRow
        key={`mealwalk-${w.meal}`}
        title={`Walk after ${w.meal}`}
        subtitle={w.state === "done" ? "Done" : `10 minutes · about ${w.at}`}
        icon={w.state === "done" ? I.check : I.footsteps}
        tone={w.state === "done" ? "success" : "sky"}
        to="/train/walks"
      />
    ));

  const foodRow =
    food && ledger ? (
      <NavRow
        key="food"
        title="Food"
        subtitle={
          ledger.in === null
            ? "Nothing logged"
            : `${ledger.in.toLocaleString()} in${
                ledger.remaining === null
                  ? ""
                  : ledger.remaining >= 0
                    ? ` · ~${ledger.remaining.toLocaleString()} left`
                    : ` · ~${Math.abs(ledger.remaining).toLocaleString()} over`
              }`
        }
        icon={I.bowl}
        to="/food"
      />
    ) : null;

  // A row value, never a strip figure: Food owns water in full (ADR-029, ADR-044).
  const waterRow = food ? (
    <NavRow key="water" title="Water" subtitle={waterRowValue(S, ds)} icon={I.glass} to="/food/water" />
  ) : null;

  const wi = weighInDue(S, ds);
  // The reading itself, not the smoothed trend: the trend lags a daily weigher by ~9 days and
  // read as "the previous weight" the day after a weigh-in. Progress carries the trend.
  const ws = weightSeries(S);
  const latestW = ws.length ? ws[ws.length - 1].v : null;
  const agoWords = (n: number | null) =>
    n === null ? "" : n === 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`;
  const weighLine =
    wi.lastDs === null
      ? "No weigh-in yet"
      : wi.due
        ? `Due — last weighed ${agoWords(wi.daysAgo)}`
        : `${latestW === null ? "–" : latestW.toFixed(1)} kg · ${agoWords(wi.daysAgo)}`;
  const weighRow = (
    <NavRow key="weigh" title="Weigh-in" subtitle={weighLine} icon={I.scale} onClick={() => setSheet("weight")} />
  );

  const ph = ci ? cyclePhase(S, ds) : null;
  const cycleLine = (() => {
    if (!ci) return null;
    switch (cycleVariant(ci)) {
      case "period": {
        // what she logged, never a phase (ADR-057)
        const felt = loggedWords(S, ds);
        return `Period day ${ci.day ?? "–"}${L?.periodSkip ? " · resting" : ""}${felt.length ? ` · ${felt.join(", ")} logged` : ""}`;
      }
      case "expected":
        return "Period expected around now";
      case "pms":
        return `${ci.daysToNext} day${ci.daysToNext === 1 ? "" : "s"} to your predicted period`;
      case "unknown":
        return "Tell Kaya when your period starts";
      default:
        return `Day ${ci.day}${ph ? ` · ${ph.phase.n.toLowerCase()}` : ""}${ci.next ? ` · next ~${dayMonth(ci.next)}` : ""}`;
    }
  })();
  const cycleRow = cycleLine ? (
    <NavRow
      key="cycle"
      title="Cycle"
      subtitle={cycleLine}
      icon={I.drop}
      tone="cycle"
      onClick={() => setSheet("cycle")}
    />
  ) : null;

  const recapRow = recapDue(S, today) ? (
    <NavRow
      key="recap"
      title="Last week"
      subtitle={`${weekStats(S, 1, today).sessions}/6 sessions · see the recap`}
      icon={I.calendar}
      onClick={() => {
        // Opening it is looking at it: the row goes once the recap has been seen.
        update((d) => void (d.recapSeen[weekKey(today)] = 1));
        history.push("/progress/training");
      }}
    />
  ) : null;

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Today" sub={`${dayLabel(ds)} · week ${weekNo(S)}, phase ${p + 1}`} action={<SyncDot />} />
          <div className="today">
            {updateNotice}
            {setupFirst ? setupFirst.el : null}
            {hero}
            {after.map((n) => n.el)}

            <StatStrip ariaLabel="Today in figures">
              <Stat
                figure={steps.basis === "none" ? null : steps.total}
                label="Steps"
                tag={stepsTone ? <Tag tone={stepsTone} /> : undefined}
                to="/today/activity"
              />
              <Stat figure={work.total} label="Work · kcal" estimated={work.total !== null} to="/today/activity" />
              {ledger ? (
                <Stat
                  figure={ledger.remaining === null ? null : `~${Math.abs(ledger.remaining).toLocaleString()}`}
                  label={ledger.remaining !== null && ledger.remaining < 0 ? "Over · kcal" : "Left · kcal"}
                  note={ledger.budget !== null ? `of ~${ledger.budget.toLocaleString()}` : undefined}
                  to="/food"
                />
              ) : (
                <Stat
                  figure={moveKnown ? ring.move.value : null}
                  label="Move · min"
                  note={`goal ${ring.move.goal}`}
                  to="/today/activity"
                />
              )}
            </StatStrip>

            <RowGroup heading="Also today">
              {sessionRow}
              {treadmillRow}
              {walksRow}
              {mealWalkRows}
              {foodRow}
              {waterRow}
              {weighRow}
              {cycleRow}
              {recapRow}
            </RowGroup>
          </div>
        </Screen>

        <ChangeTodaySheet open={sheet === "change"} onClose={() => setSheet(null)} ds={ds} />
        <LogWeightSheet open={sheet === "weight"} onClose={() => setSheet(null)} />
        <CycleSheet open={sheet === "cycle"} onClose={() => setSheet(null)} ds={ds} />
        <SetupSheet open={sheet === "setup"} onClose={() => setSheet(null)} ds={ds} />
      </IonContent>
    </IonPage>
  );
}
